import { AlertTriangle, ChevronRight, RotateCcw } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { PageMain } from '@/components/page-main'

/**
 * Full-bleed error screen used by the router's `defaultErrorComponent` and
 * route-level error boundaries. Matches the boot / onboarding / login
 * aesthetic so unexpected failures still feel like part of the product
 * instead of dumping a raw stack trace on a white screen.
 */
export function ErrorScreen({
  error,
  reset,
  title = 'Something went wrong',
  eyebrow = 'Unexpected error',
}: {
  error: unknown
  /** When provided, shows a "Try again" button that calls this. */
  reset?: () => void
  title?: string
  eyebrow?: string
}) {
  const [showDetails, setShowDetails] = useState(false)
  const message = errorMessage(error)
  const stack = error instanceof Error ? error.stack : null

  return (
    <PageMain className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background p-6">
      <div className="absolute inset-0 hawk-grid pointer-events-none" aria-hidden />
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 30%, rgba(244, 63, 94, 0.08), transparent 70%)',
        }}
      />

      <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.32em] text-muted-foreground flex items-center gap-4">
        <span className="text-foreground/80">Hawk</span>
        <span className="text-destructive/80">{eyebrow}</span>
      </div>

      <div className="relative w-full max-w-md animate-hawk-rise">
        <div
          role="alert"
          className="border border-border bg-card/80 backdrop-blur p-7 rounded-md text-center space-y-5"
        >
          <div className="flex items-center justify-center">
            <span className="inline-flex items-center justify-center size-12 rounded-md bg-destructive/10 border border-destructive/30">
              <AlertTriangle
                className="size-5 text-destructive"
                strokeWidth={1.5}
              />
            </span>
          </div>

          <div className="space-y-2">
            <h1 className="text-xl font-medium tracking-[-0.02em]">{title}</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {message}
            </p>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            {reset && (
              <Button onClick={reset} className="w-full gap-2">
                <RotateCcw className="size-3.5" strokeWidth={1.5} />
                Try again
              </Button>
            )}
            <Button
              asChild
              variant={reset ? 'outline' : 'default'}
              className="w-full gap-2"
            >
              <Link to="/dashboard">
                Back to dashboard
                <ChevronRight className="size-3.5" strokeWidth={1.5} />
              </Link>
            </Button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground/70 hover:text-muted-foreground transition-colors mt-1"
            >
              or reload the page
            </button>
          </div>

          {stack && (
            <div className="pt-3 border-t border-border text-left">
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 hover:text-foreground transition-colors"
              >
                {showDetails ? 'Hide' : 'Show'} technical details
              </button>
              {showDetails && (
                <pre className="mt-3 max-h-48 overflow-auto text-[10px] font-mono leading-relaxed bg-background/60 rounded-sm border border-border p-3 text-muted-foreground whitespace-pre-wrap break-all">
                  {stack}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </PageMain>
  )
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message || 'Unknown error.'
  if (typeof error === 'string' && error) return error
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message
    if (typeof m === 'string' && m) return m
  }
  return 'An unexpected error occurred while rendering this page.'
}
